import {AutoLinkNode, LinkNode} from '@lexical/link';
import {DOMExportOutputMap, LexicalEditor, LexicalNode, TextNode} from 'lexical';
import {
  BeautifulMentionComponentProps,
  BeautifulMentionNode,
  BeautifulMentionsMenuItemProps,
  createBeautifulMentionNode,
} from 'lexical-beautiful-mentions';
import {
  CustomMenuItem,
  FormControl,
  FormField,
  StyledContentEditable,
  Tooltip,
} from 'platform/components';
import {Integer} from 'platform/foundation';
import {css, styled} from 'styled-components';
import {match} from 'ts-pattern';

import {forwardRef} from 'react';
import {Path} from 'react-hook-form';

import {compose, isNil, toLower, prop, sortBy} from 'ramda';

import {TemplateContextEnum, useListContextParametersQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {getVariables} from '@dms/shared';

import {Nullish} from 'shared/web';

import {FormValues} from '../types/FormValues';

interface TemplateBodyFieldProps {
  control: FormControl<FormValues>;
  name: Path<FormValues>;
  context: TemplateContextEnum | Nullish;
  label: string;
  isDisabled?: boolean;
  minHeight?: Integer;
}

const ContentEditableComponent = styled(StyledContentEditable)<{
  $isMessage: boolean;
  $isDisabled?: boolean;
}>`
  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      pointer-events: none;
    `}

  .mentions {
    display: inline-block;
    background-color: ${({theme}) => theme.colors.palettes.blue[30][100]};
    border-radius: ${({theme}) => theme.radii.large};
    padding: ${({theme}) => theme.getSize(1)} ${({theme}) => theme.getSize(2)};
    transition: all 250ms;

    &:hover {
      background-color: ${({theme}) => theme.colors.palettes.blue[40][100]};
    }
  }
`;

// This is needed because Tooltip component wrap itself into <div>
const CustomMentionWrapper = styled.span`
  & > div {
    display: inline-block;
  }
`;

interface MenuItemComponentProps extends BeautifulMentionsMenuItemProps {
  ref?: React.Ref<HTMLLIElement>;
}

const MenuItemComponent = ({selected, ref, item, ...props}: MenuItemComponentProps) => {
  const component = <CustomMenuItem ref={ref} item={item} selected={selected} {...props} />;

  if (selected) {
    return (
      <Tooltip label={item.data?.description} isOpen placement="right" offset={14}>
        {component}
      </Tooltip>
    );
  }

  return component;
};

const CustomBeautifulMentionComponent = forwardRef<
  HTMLSpanElement,
  BeautifulMentionComponentProps<{id: string; description: string}>
>(({data, value, ...other}, ref) => (
  <CustomMentionWrapper ref={ref} {...other}>
    <Tooltip label={data?.description} placement="top">
      {value}
    </Tooltip>
  </CustomMentionWrapper>
));

const linkFormatter = (_editor: LexicalEditor, node: LexicalNode) => {
  const convertedNode = node as AutoLinkNode;
  const element = document.createTextNode(convertedNode.getURL());
  return {element};
};

const exportHtmlNodeTransforms = (nodes: DOMExportOutputMap) => {
  nodes.set(CustomMention[0], (_editor, node) => {
    const convertedNode = node as BeautifulMentionNode;
    const element = document.createTextNode(`{{ ${convertedNode.getData?.()?.code} }}`);
    return {element};
  });
  nodes.set(AutoLinkNode, linkFormatter);
  nodes.set(LinkNode, linkFormatter);

  return nodes;
};

const CustomMention = createBeautifulMentionNode(CustomBeautifulMentionComponent);

export function TemplateBodyField(props: TemplateBodyFieldProps) {
  const {data, isLoading} = useListContextParametersQuery(
    {
      context: props.context as TemplateContextEnum,
    },
    {
      skip: isNil(props.context),
    }
  );

  const contextParameters = sortBy(
    compose(toLower, prop('value')),
    data?.map((param) => ({
      value: param.label,
      id: param.code,
      ...param,
    })) || []
  );

  const transformStringToNodes = (value: string | null) => {
    if (!value) {
      return [];
    }
    return getVariables(value).map((val) =>
      match(val.type)
        .with('variable', () => {
          const contextParameter = contextParameters.find((param) => param.code === val.text);
          return new CustomMention[0]('/', contextParameter?.label || '', contextParameter);
        })
        .with('text', () => new TextNode(val.text || ''))
        .exhaustive()
    );
  };

  return (
    <FormField
      name={props.name}
      type="suggestionInput"
      control={props.control}
      key={props.name}
      label={props.label}
      ContentEditableComponent={ContentEditableComponent}
      isLoadingOptions={isLoading}
      minHeight={props.minHeight}
      isDisabled={props.isDisabled}
      prefix="/"
      options={contextParameters}
      MenuItemComponent={MenuItemComponent}
      exportHtmlNodeTransforms={exportHtmlNodeTransforms}
      transformStringToNodes={transformStringToNodes}
      CustomMention={CustomMention}
      placeholder={i18n.t('page.messageTemplates.labels.placeholderHint')}
    />
  );
}
